import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { List, ListRowRenderer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import styled from 'styled-components';

import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import ContentSpinner from '@/components/ContentSpinner';
import { PageHeading, SectionHeading } from '@/components/Layout/Parts';
import PulsingLoader from '@/components/PulsingLoader';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { SocialPostsImport } from '@/types/resources';
import { api } from '@/utils/api';
import { SocialPostsImportStatus } from '@/utils/enums';
import { scrollBarStyles } from '@/utils/theme';

import ExportForm from './component/ExportForm';
import PostRow, {
  Checkbox,
  CheckboxHolder,
  CheckboxLabel,
} from './component/PostRow';

const LoaderHolder = styled.div`
  text-align: center;
  padding: 60px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
  margin-bottom: 30px;
`;

const LoaderTextHolder = styled.div`
  margin: 0 auto;
`;

const LoaderTitle = styled(Text)`
  margin-top: 20px;
  margin-bottom: 6px;
`;

const ResultsCard = styled.div`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
`;

const StyledList = styled(List)`
  ${scrollBarStyles};
`;

const Headings = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.grayscaleExtraLight};
  padding: 24px;
  gap: 24px;
  align-items: center;
`;

const ImageHeadingColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const UploadButtonHolder = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  right: 120px;
  z-index: 2;
`;

const UploadButton = styled(Button)`
  padding: 0 24px;
  box-shadow:
    0px 10px 30px rgba(34, 34, 34, 0.12),
    0px 20px 100px rgba(34, 34, 34, 0.06);
  min-width: 284px;
`;

const StyledCheckbox = styled(Checkbox)`
  &:checked + ${CheckboxLabel} {
    &::after {
      content: '';
      position: absolute;
      left: 8px;
      top: 3px;
      width: 8px;
      height: 14px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 0 0;
      transform: rotate(90deg);
    }
  }
`;

const ResultsPage = () => {
  const { importId } = useParams();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [uncheckedPosts, setUncheckedPosts] = useState<number[]>([]);

  const { data: postsImport, isLoading } = useQuery(
    [
      'social_posts_imports',
      {
        id: importId,
      },
    ],
    () =>
      api.get<never, SocialPostsImport>(
        `/v1/tools/social-posts-imports/${importId}`,
      ),
    {
      retry: 2,
      refetchInterval: (data) => {
        if (data?.status === SocialPostsImportStatus.PENDING) {
          return 10_000;
        }

        return false;
      },
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate('/404', {
            replace: true,
          });
        }
        alerts.error();
      },
    },
  );

  const renderRow: ListRowRenderer = useCallback(
    ({ index, key, style, isVisible }) => {
      const post = postsImport!.posts![index];

      if (!isVisible) return null;

      return (
        <PostRow
          key={key}
          style={style}
          post={post}
          onCheck={() =>
            setUncheckedPosts((prev) =>
              prev.includes(post.id)
                ? prev.filter((p) => p !== post.id)
                : [...prev, post.id],
            )
          }
          isChecked={!uncheckedPosts.includes(post.id)}
        />
      );
    },
    [postsImport, uncheckedPosts],
  );

  const onToggleAll = () => {
    setUncheckedPosts((prev) => {
      if (!prev.length) {
        return postsImport?.posts?.map((p) => p.id) ?? [];
      }

      return [];
    });
  };

  const exportableIds = useMemo(() => {
    return (
      postsImport?.posts
        ?.map((p) => p.id)
        ?.filter((p) => !uncheckedPosts.includes(p)) ?? []
    );
  }, [postsImport?.posts, uncheckedPosts]);

  if (isLoading || !postsImport) {
    return <ContentSpinner />;
  }

  return (
    <>
      <Text $type="caption" $color="gray">
        {postsImport.targetUrl}
      </Text>
      <PageHeading>Social Posts Importer</PageHeading>
      <BackButton />
      {postsImport.status === SocialPostsImportStatus.PENDING && (
        <LoaderHolder>
          <PulsingLoader />
          <LoaderTextHolder>
            <LoaderTitle $type="button">Collecting posts ...</LoaderTitle>
            <Text $color="gray" $type="captionSemibold">
              Please be patient, this action may take
              <br />a little longer in some cases
            </Text>
          </LoaderTextHolder>
        </LoaderHolder>
      )}
      <AnimatePresence>
        {uncheckedPosts.length !== postsImport.posts!.length && (
          <UploadButtonHolder
            initial={{ scale: 0, opacity: 0.3 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0.3 }}
          >
            <UploadButton
              onClick={() => setShowUploadModal(true)}
              color="inverted"
            >
              Upload to BP ({postsImport.posts!.length - uncheckedPosts.length})
            </UploadButton>
          </UploadButtonHolder>
        )}
      </AnimatePresence>
      <SectionHeading>Posts list ({postsImport.posts!.length})</SectionHeading>
      <ResultsCard>
        <Headings>
          <ImageHeadingColumn>
            <CheckboxHolder>
              <StyledCheckbox
                id={`post-all`}
                name="products"
                type="checkbox"
                checked={!uncheckedPosts.length}
                onChange={onToggleAll}
              />
              <CheckboxLabel htmlFor={`post-all`} />
            </CheckboxHolder>
            <Text $type="smallBold">Image</Text>
          </ImageHeadingColumn>
          <Text $type="smallBold">Message</Text>
          <Text $type="smallBold">Likes</Text>
          <Text $type="smallBold">Author</Text>
        </Headings>
        {!!postsImport.posts!.length && (
          <StyledList
            width={1120}
            height={800}
            headerHeight={200}
            rowHeight={140}
            rowRenderer={renderRow}
            rowCount={postsImport.posts!.length}
          />
        )}
      </ResultsCard>
      <AnimatePresence>
        {showUploadModal && (
          <ExportForm
            importId={postsImport.id}
            onClose={() => setShowUploadModal(false)}
            postIds={exportableIds}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ResultsPage;
